import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { siteConfig } from '../data/site-content';
import { 
  FileText, 
  User, 
  ClipboardList, 
  Target, 
  Calendar, 
  Cookie, 
  Network, 
  Archive, 
  Shield, 
  Lock, 
  AlertTriangle, 
  Edit, 
  Phone, 
  Mail, 
  MapPin, 
  Briefcase,
  ChevronRight
} from 'lucide-react';

const Privatlivspolitik = () => {
  const [activeSection, setActiveSection] = useState('introduktion');

  const menuItems = [
    { id: 'introduktion', label: 'Privatlivspolitik', icon: FileText },
    { id: 'dataansvarlig', label: 'Dataansvarlig', icon: User },
    { id: 'oplysninger', label: 'Hvilke oplysninger jeg indsamler', icon: ClipboardList },
    { id: 'formaal', label: 'Formål med behandlingen', icon: Target },
    { id: 'booking', label: 'Booking og betaling', icon: Calendar },
    { id: 'cookies', label: 'Cookies og tracking', icon: Cookie },
    { id: 'databehandlere', label: 'Databehandlere', icon: Network },
    { id: 'opbevaring', label: 'Opbevaring og sletning', icon: Archive },
    { id: 'rettigheder', label: 'Dine rettigheder', icon: Shield },
    { id: 'klage', label: 'Klage', icon: AlertTriangle },
    { id: 'aendringer', label: 'Ændringer', icon: Edit },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      for (const item of menuItems) {
        const element = document.getElementById(item.id);
        if (element) {
          const top = element.offsetTop;
          const height = element.offsetHeight;

          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(item.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Offset to account for header/navbar
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(id);
    }
  };

  return (
    <>
      <Helmet>
        <title>Privatlivspolitik – CoreHypnose v/ Lars Nissen Corell – Psykoterapeut MPF</title>
        <meta name="description" content="Læs privatlivspolitikken for CoreHypnose v/ Lars Nissen Corell – Psykoterapeut MPF. Se hvordan jeg behandler dine personoplysninger fortroligt og med respekt for dit privatliv." />
      </Helmet>

      {/* Hero / Header */}
      <section className="pt-20 pb-0 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-6 text-left">
          <h2 className="text-[#8C2F12] font-bold uppercase tracking-[0.2em] mb-4 text-sm">Fortrolighed & Sikkerhed</h2>
          <h1 className="text-4xl md:text-5xl font-serif text-[#123F13] mb-6 uppercase">PRIVATLIVSPOLITIK</h1>
          <p className="text-lg md:text-xl text-[#3B352F]/80 max-w-3xl leading-relaxed mb-6">
            Hvad jeg indsamler, hvorfor jeg gør det, og hvilke rettigheder du har.
          </p>
          
          {/* Intro Paragraph moved here */}
          <p className="text-[#3B352F]/90 text-base leading-relaxed max-w-3xl mb-8">
            Hos CoreHypnose passer jeg godt på dine personoplysninger. Når du kontakter mig, booker en tid eller bruger min hjemmeside, behandler jeg kun de oplysninger, der er nødvendige for at kunne besvare din henvendelse, administrere din booking, gennemføre dit forløb og overholde gældende lovgivning.
          </p>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="pb-16 md:pb-24 pt-8 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-6">
          
          <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-12 items-start">
            
            {/* Left Column: Sticky Table of Contents */}
            <aside className="hidden lg:block sticky top-28 bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm">
              <h3 className="text-xs uppercase font-bold tracking-wider text-[#3B352F]/50 mb-4 pb-2 border-b border-[#FAF7F2]">
                Indhold
              </h3>
              <nav className="space-y-1">
                {menuItems.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = activeSection === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`w-full flex items-center gap-3 py-2.5 px-3 rounded-xl text-left text-sm transition-all duration-200 ${
                        isActive 
                          ? 'bg-[#123F13] text-white font-semibold shadow-sm' 
                          : 'text-[#3B352F]/70 hover:text-[#123F13] hover:bg-[#FAF7F2]'
                      }`}
                    >
                      <IconComponent size={16} className={isActive ? 'text-white' : 'text-[#123F13]/70'} />
                      <span className="truncate">{item.label}</span>
                    </button>
                  );
                })}
              </nav>
            </aside>

            {/* Right Column: Main Content Cards */}
            <div className="space-y-8">
              
              {/* Contact Card (Top of Main Area) */}
              <div id="kontakt-kort" className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm scroll-mt-24">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="w-12 h-12 shrink-0 bg-[#123F13] text-white flex items-center justify-center rounded-xl shadow-md">
                    <Mail size={22} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-xl md:text-2xl font-bold text-[#123F13] mb-4">Kontakt</h3>
                    <p className="font-bold text-[#123F13] text-lg mb-2">CoreHypnose v/ Lars Nissen Corell – Psykoterapeut MPF</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm text-[#3B352F]/80">
                      <div className="flex items-center gap-3">
                        <MapPin size={18} className="text-[#8C2F12] shrink-0" />
                        <span>Valby Tingsted 7, 2500 Valby</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Briefcase size={18} className="text-[#8C2F12] shrink-0" />
                        <span>CVR: 41913843</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail size={18} className="text-[#8C2F12] shrink-0" />
                        <a href="mailto:kontakt@corehypnose.dk" className="hover:text-[#123F13] transition-colors font-medium underline decoration-[#E8DDCE] hover:decoration-[#123F13]">
                          kontakt@corehypnose.dk
                        </a>
                      </div>
                      <div className="flex items-center gap-3">
                        <Phone size={18} className="text-[#8C2F12] shrink-0" />
                        <a href="tel:27204671" className="hover:text-[#123F13] transition-colors font-medium">
                          27 20 46 71
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 1: Privatlivspolitik for CoreHypnose */}
              <div id="introduktion" className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm scroll-mt-24 transition-all hover:shadow-md">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="w-10 h-10 shrink-0 bg-[#123F13] text-white flex items-center justify-center rounded-xl">
                    <FileText size={20} />
                  </div>
                  <div className="flex-1 space-y-4">
                    <h3 className="font-serif text-lg md:text-xl font-bold text-[#123F13]">
                      Privatlivspolitik for CoreHypnose
                    </h3>
                    <div className="text-sm text-[#3B352F]/80 leading-relaxed space-y-3">
                      <p>
                        Hos CoreHypnose behandler jeg dine personoplysninger fortroligt og med respekt for dit privatliv.
                      </p>
                      <p>
                        Denne privatlivspolitik beskriver, hvilke oplysninger jeg kan indsamle om dig, hvorfor jeg behandler dem, hvordan de opbevares, og hvilke rettigheder du har.
                      </p>
                      <p>
                        Politikken gælder, når du besøger min hjemmeside, kontakter mig, booker en tid, betaler for en ydelse eller indgår i et behandlingsforløb hos CoreHypnose.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: Dataansvarlig */}
              <div id="dataansvarlig" className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm scroll-mt-24 transition-all hover:shadow-md">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="w-10 h-10 shrink-0 bg-[#123F13] text-white flex items-center justify-center rounded-xl">
                    <User size={20} />
                  </div>
                  <div className="flex-1 space-y-4">
                    <h3 className="font-serif text-lg md:text-xl font-bold text-[#123F13]">
                      Dataansvarlig
                    </h3>
                    <div className="text-sm text-[#3B352F]/80 leading-relaxed space-y-3">
                      <p>
                        Den dataansvarlige for behandlingen af dine personoplysninger er:
                      </p>
                      <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE] space-y-1 font-medium text-[#123F13]">
                        <p>CoreHypnose v/ Lars Nissen Corell</p>
                        <p>Valby Tingsted 7, 2500 Valby</p>
                        <p>CVR: 41913843</p>
                        <p>E-mail: kontakt@corehypnose.dk</p>
                        <p>Telefon: 27 20 46 71</p>
                      </div>
                      <p>
                        Det betyder, at CoreHypnose er ansvarlig for, hvordan dine oplysninger behandles, når du kontakter klinikken eller bruger hjemmesiden.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: Hvilke oplysninger jeg indsamler */}
              <div id="oplysninger" className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm scroll-mt-24 transition-all hover:shadow-md">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="w-10 h-10 shrink-0 bg-[#123F13] text-white flex items-center justify-center rounded-xl">
                    <ClipboardList size={20} />
                  </div>
                  <div className="flex-1 space-y-4">
                    <h3 className="font-serif text-lg md:text-xl font-bold text-[#123F13]">
                      Hvilke oplysninger jeg indsamler & Hvor de kommer fra
                    </h3>
                    <div className="text-sm text-[#3B352F]/80 leading-relaxed space-y-4">
                      <div>
                        <p className="mb-2">
                          Jeg kan indsamle og behandle almindelige personoplysninger, når du kontakter mig, booker en tid eller indgår i et forløb. Det kan for eksempel være:
                        </p>
                        <ul className="list-disc pl-5 space-y-1 marker:text-[#8C2F12]">
                          <li>Navn</li>
                          <li>Telefonnummer</li>
                          <li>E-mailadresse</li>
                          <li>Besked sendt via kontaktformular</li>
                          <li>Bookingoplysninger</li>
                          <li>Betalings- og fakturaoplysninger</li>
                          <li>Kommunikation via e-mail, telefon, SMS, Messenger eller sociale medier</li>
                        </ul>
                      </div>

                      <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE] border-l-4 border-l-[#8C2F12]">
                        <p className="font-semibold text-[#123F13] mb-1">Følsomme personoplysninger</p>
                        <p className="text-xs md:text-sm">
                          Hvis du selv vælger at skrive oplysninger om din situation, dit helbred, din psykiske trivsel, angst, stress, depression, søvn, uro, traumer eller andre personlige forhold, kan der være tale om følsomme personoplysninger. Sådanne oplysninger behandles fortroligt og kun i det omfang, det er nødvendigt for at kunne besvare din henvendelse eller gennemføre et relevant forløb.
                        </p>
                      </div>

                      <div className="pt-2">
                        <h4 className="font-bold text-[#123F13] mb-2">Hvor oplysningerne kommer fra:</h4>
                        <p className="mb-2">Jeg kan modtage personoplysninger gennem:</p>
                        <ul className="list-disc pl-5 space-y-1 marker:text-[#8C2F12]">
                          <li>Kontaktformular på hjemmesiden</li>
                          <li>EasyPractice</li>
                          <li>E-mail</li>
                          <li>Telefon og SMS</li>
                          <li>Facebook- og Instagram-leadformularer</li>
                          <li>Messenger</li>
                          <li>Booking- og betalingssystemer</li>
                          <li>Direkte dialog med dig</li>
                        </ul>
                        <p className="mt-3 text-xs text-[#3B352F]/60 italic">
                          Du bestemmer selv, hvilke oplysninger du sender til mig. Jeg anbefaler, at du undgår at sende meget følsomme oplysninger via sociale medier eller åbne beskedkanaler.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 4: Formål med behandlingen */}
              <div id="formaal" className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm scroll-mt-24 transition-all hover:shadow-md">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="w-10 h-10 shrink-0 bg-[#123F13] text-white flex items-center justify-center rounded-xl">
                    <Target size={20} />
                  </div>
                  <div className="flex-1 space-y-4">
                    <h3 className="font-serif text-lg md:text-xl font-bold text-[#123F13]">
                      Formål med behandlingen & Retsgrundlag
                    </h3>
                    <div className="text-sm text-[#3B352F]/80 leading-relaxed space-y-4">
                      <div>
                        <p className="mb-2">Jeg behandler dine personoplysninger for at kunne:</p>
                        <ul className="list-disc pl-5 space-y-1 marker:text-[#8C2F12]">
                          <li>Besvar din henvendelse</li>
                          <li>Kontakte dig om booking eller ændringer</li>
                          <li>Administrere aftaler og klientforløb</li>
                          <li>Gennemføre sessioner og behandling</li>
                          <li>Sende praktiske beskeder om tider, betaling og forløb</li>
                          <li>Håndtere betaling, fakturering og bogføring</li>
                          <li>Følge op på henvendelser fra kontaktformularer eller sociale medier</li>
                          <li>Forbedre hjemmesiden og brugeroplevelsen</li>
                          <li>Overholde gældende lovgivning</li>
                        </ul>
                        <p className="mt-2 text-xs text-[#3B352F]/60">
                          Jeg behandler kun oplysninger, der er relevante for det konkrete formål.
                        </p>
                      </div>

                      <div className="pt-2">
                        <h4 className="font-bold text-[#123F13] mb-2">Retsgrundlag</h4>
                        <p className="mb-2">Behandlingen af almindelige personoplysninger sker typisk på baggrund af:</p>
                        <ul className="list-disc pl-5 space-y-1.5 marker:text-[#8C2F12]">
                          <li>At behandlingen er nødvendig for at kunne indgå eller opfylde en aftale med dig</li>
                          <li>At jeg har en legitim interesse i at kunne besvare din henvendelse og administrere min klinik</li>
                          <li>At behandlingen er nødvendig for at overholde retlige forpligtelser, for eksempel bogføringsregler</li>
                        </ul>
                        <p className="mt-3">
                          Hvis der behandles følsomme oplysninger, sker det kun, når det er nødvendigt i forbindelse med dit ønske om hjælp, terapi, hypnose eller rådgivning, eller når du selv har givet oplysningerne til mig.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 5: Booking og betaling */}
              <div id="booking" className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm scroll-mt-24 transition-all hover:shadow-md">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="w-10 h-10 shrink-0 bg-[#123F13] text-white flex items-center justify-center rounded-xl">
                    <Calendar size={20} />
                  </div>
                  <div className="flex-1 space-y-4">
                    <h3 className="font-serif text-lg md:text-xl font-bold text-[#123F13]">
                      Booking, betaling & Kommunikation
                    </h3>
                    <div className="text-sm text-[#3B352F]/80 leading-relaxed space-y-4">
                      <div>
                        <p className="mb-2">
                          Når du booker en tid hos CoreHypnose, kan dine oplysninger blive behandlet i EasyPractice eller andre relevante systemer. Her kan der blandt andet blive registreret:
                        </p>
                        <ul className="list-disc pl-5 space-y-1 marker:text-[#8C2F12]">
                          <li>Navn</li>
                          <li>Kontaktoplysninger</li>
                          <li>Tidspunkt for aftale</li>
                          <li>Valgt ydelse</li>
                          <li>Betalingsstatus</li>
                          <li>Fakturaoplysninger</li>
                          <li>Eventuelle praktiske oplysninger om aftalen</li>
                        </ul>
                      </div>

                      <div>
                        <p className="mb-2">Betaling kan ske via blandt andet:</p>
                        <ul className="list-disc pl-5 space-y-1 marker:text-[#8C2F12]">
                          <li>MobilePay</li>
                          <li>Kortbetaling</li>
                          <li>Bankoverførsel</li>
                          <li>Faktura</li>
                          <li>EasyPractice betalingssystem</li>
                        </ul>
                        <p className="mt-2">
                          Betalings- og fakturaoplysninger opbevares i det omfang, det er nødvendigt for administration, dokumentation og bogføring.
                        </p>
                      </div>

                      <div className="pt-2 border-t border-[#FAF7F2]">
                        <h4 className="font-bold text-[#123F13] mb-2">Kommunikation</h4>
                        <p>
                          Hvis du kontakter mig via e-mail, telefon, SMS, Messenger, Facebook, Instagram eller kontaktformular, behandler jeg de oplysninger, du selv sender til mig. Formålet er at kunne svare dig, følge op på din henvendelse og eventuelt aftale en tid.
                        </p>
                        <p className="mt-2 text-xs text-[#3B352F]/60 italic">
                          Jeg anbefaler, at du ikke sender meget følsomme oplysninger via sociale medier, Messenger eller SMS. Ved mere personlige oplysninger kan det være bedre at tage dem direkte i sessionen eller via en mere egnet kanal.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 6: Cookies og tracking */}
              <div id="cookies" className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm scroll-mt-24 transition-all hover:shadow-md">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="w-10 h-10 shrink-0 bg-[#123F13] text-white flex items-center justify-center rounded-xl">
                    <Cookie size={20} />
                  </div>
                  <div className="flex-1 space-y-4">
                    <h3 className="font-serif text-lg md:text-xl font-bold text-[#123F13]">
                      Cookies, statistik, markedsføring & Sociale Medier
                    </h3>
                    <div className="text-sm text-[#3B352F]/80 leading-relaxed space-y-4">
                      <div>
                        <p className="mb-2">
                          Hjemmesiden kan anvende cookies og lignende teknologier. Cookies kan bruges til teknisk funktionalitet, statistik, analyse og markedsføring. Det kan blandt andet hjælpe mig med at forstå, hvordan hjemmesiden bliver brugt, og hvordan jeg kan forbedre indhold og brugeroplevelse.
                        </p>
                        <p className="mb-2">Hjemmesiden kan anvende værktøjer som:</p>
                        <ul className="list-disc pl-5 space-y-1 marker:text-[#8C2F12]">
                          <li>Google Analytics</li>
                          <li>Meta Pixel / Facebook Pixel</li>
                          <li>Google Ads tracking</li>
                          <li>Indlejrede YouTube-videoer</li>
                        </ul>
                        <p className="mt-2">
                          Du kan afvise eller tilpasse cookies via hjemmesidens cookiebanner. Du kan også slette eller blokere cookies i din browser. Nogle funktioner på hjemmesiden kan fungere dårligere, hvis cookies fravælges.
                        </p>
                      </div>

                      <div className="pt-2 border-t border-[#FAF7F2]">
                        <h4 className="font-bold text-[#123F13] mb-2">Facebook, Instagram og leadformularer</h4>
                        <p className="mb-2">
                          Hvis du kontakter mig via Facebook, Instagram, Messenger eller en leadformular, behandler jeg de oplysninger, du selv afgiver. Det kan for eksempel være:
                        </p>
                        <ul className="list-disc pl-5 space-y-1 marker:text-[#8C2F12]">
                          <li>Navn</li>
                          <li>Telefonnummer</li>
                          <li>E-mailadresse</li>
                          <li>Svar i formularen</li>
                          <li>Eventuelle beskeder, du sender</li>
                        </ul>
                        <p className="mt-3">
                          Meta/Facebook kan samtidig behandle oplysninger om dig efter deres egne vilkår og privatlivspolitik. Jeg anbefaler, at du læser Metas privatlivspolitik, hvis du ønsker mere information om deres behandling af data.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 7: Databehandlere */}
              <div id="databehandlere" className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm scroll-mt-24 transition-all hover:shadow-md">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="w-10 h-10 shrink-0 bg-[#123F13] text-white flex items-center justify-center rounded-xl">
                    <Network size={20} />
                  </div>
                  <div className="flex-1 space-y-4">
                    <h3 className="font-serif text-lg md:text-xl font-bold text-[#123F13]">
                      Databehandlere og eksterne leverandører
                    </h3>
                    <div className="text-sm text-[#3B352F]/80 leading-relaxed space-y-3">
                      <p className="mb-2">
                        Jeg kan bruge eksterne leverandører og systemer til drift af hjemmeside, booking, betaling, kommunikation, markedsføring, statistik og administration. Det kan blandt andet være:
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE] text-xs md:text-sm font-medium text-[#123F13]">
                        <ul className="list-disc pl-5 space-y-1 marker:text-[#8C2F12]">
                          <li>EasyPractice</li>
                          <li>Webhotel og hosting</li>
                          <li>WordPress / Elementor</li>
                          <li>Google / Meta / Facebook / Instagram</li>
                        </ul>
                        <ul className="list-disc pl-5 space-y-1 marker:text-[#8C2F12]">
                          <li>MobilePay & Betalingsudbydere</li>
                          <li>E-mailudbydere</li>
                          <li>Revisor eller bogholder</li>
                          <li>
                            Auxo / Erhvervs Webdesign / Aveo / Ravn hjemmesider<br />
                            <span className="text-xs text-[#3B352F]/60 font-normal">John Tranums Vej 23, 6705 Esbjerg</span>
                          </li>
                        </ul>
                      </div>
                      <p>
                        Disse leverandører må kun behandle personoplysninger i det omfang, det er nødvendigt for at levere deres ydelse.
                      </p>
                      <p className="font-semibold text-[#8C2F12]">
                        Jeg sælger ikke dine personoplysninger til tredjeparter.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 8: Opbevaring og sletning */}
              <div id="opbevaring" className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm scroll-mt-24 transition-all hover:shadow-md">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="w-10 h-10 shrink-0 bg-[#123F13] text-white flex items-center justify-center rounded-xl">
                    <Archive size={20} />
                  </div>
                  <div className="flex-1 space-y-4">
                    <h3 className="font-serif text-lg md:text-xl font-bold text-[#123F13]">
                      Opbevaring, sletning & Sikkerhed
                    </h3>
                    <div className="text-sm text-[#3B352F]/80 leading-relaxed space-y-4">
                      <div>
                        <p className="mb-2">
                          Jeg opbevarer kun dine personoplysninger, så længe det er nødvendigt i forhold til det formål, de er indsamlet til. Som udgangspunkt gælder følgende:
                        </p>
                        <ul className="list-disc pl-5 space-y-2 marker:text-[#8C2F12]">
                          <li>
                            <strong>Henvendelser:</strong> via kontaktformular, e-mail, telefon, SMS eller sociale medier opbevares kun, så længe det er relevant for at besvare og følge op på henvendelsen.
                          </li>
                          <li>
                            <strong>Booking og klientforløb:</strong> opbevares, så længe det er nødvendigt af hensyn til behandling, dokumentation og administration.
                          </li>
                          <li>
                            <strong>Betalings- og regnskabsoplysninger:</strong> opbevares som udgangspunkt i 5 år i henhold til bogføringsreglerne.
                          </li>
                        </ul>
                        <p className="mt-2 text-xs text-[#3B352F]/60">
                          Når oplysningerne ikke længere er nødvendige, bliver de slettet eller anonymiseret.
                        </p>
                      </div>

                      <div className="pt-4 border-t border-[#FAF7F2] space-y-2">
                        <div className="flex items-center gap-2 text-[#123F13] font-bold">
                          <Lock size={18} className="text-[#8C2F12]" />
                          <h4>Sikkerhed</h4>
                        </div>
                        <p>
                          Jeg behandler dine oplysninger fortroligt og træffer rimelige tekniske og organisatoriske foranstaltninger for at beskytte oplysningerne mod uautoriseret adgang, misbrug, tab eller ændring.
                        </p>
                        <p>
                          Adgang til personoplysninger begrænses til relevante systemer og leverandører, der er nødvendige for driften af klinikken.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 9: Dine rettigheder */}
              <div id="rettigheder" className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm scroll-mt-24 transition-all hover:shadow-md">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="w-10 h-10 shrink-0 bg-[#123F13] text-white flex items-center justify-center rounded-xl">
                    <Shield size={20} />
                  </div>
                  <div className="flex-1 space-y-4">
                    <h3 className="font-serif text-lg md:text-xl font-bold text-[#123F13]">
                      Dine rettigheder
                    </h3>
                    <div className="text-sm text-[#3B352F]/80 leading-relaxed space-y-3">
                      <p className="mb-2">
                        Du har efter databeskyttelsesreglerne en række rettigheder. Du har blandt andet ret til:
                      </p>
                      <ul className="list-disc pl-5 space-y-1 marker:text-[#8C2F12]">
                        <li>At få indsigt i, hvilke oplysninger jeg behandler om dig</li>
                        <li>At få rettet urigtige oplysninger</li>
                        <li>At få slettet oplysninger, når de ikke længere er nødvendige</li>
                        <li>At få begrænset behandlingen af dine oplysninger</li>
                        <li>At gøre indsigelse mod behandlingen</li>
                        <li>At få udleveret oplysninger i visse tilfælde</li>
                        <li>At trække et eventuelt samtykke tilbage</li>
                      </ul>
                      <p className="pt-2">
                        Hvis du ønsker at gøre brug af dine rettigheder, kan du kontakte mig på:
                      </p>
                      <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE] inline-block font-medium text-[#123F13]">
                        <a href="mailto:kontakt@corehypnose.dk" className="hover:underline">
                          kontakt@corehypnose.dk
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 10: Klage */}
              <div id="klage" className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm scroll-mt-24 transition-all hover:shadow-md">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="w-10 h-10 shrink-0 bg-[#123F13] text-white flex items-center justify-center rounded-xl">
                    <AlertTriangle size={20} />
                  </div>
                  <div className="flex-1 space-y-4">
                    <h3 className="font-serif text-lg md:text-xl font-bold text-[#123F13]">
                      Klage
                    </h3>
                    <div className="text-sm text-[#3B352F]/80 leading-relaxed space-y-3">
                      <p>
                        Hvis du er utilfreds med den måde, dine personoplysninger bliver behandlet på, har du ret til at klage til Datatilsynet.
                      </p>
                      <p>
                        Du kan finde mere information og kontaktoplysninger på:
                      </p>
                      <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE] inline-block font-medium text-[#123F13]">
                        <a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1">
                          www.datatilsynet.dk
                          <ChevronRight size={14} />
                        </a>
                      </div>
                      <p className="text-xs text-[#3B352F]/60 italic pt-2">
                        Du er også altid velkommen til først at kontakte mig direkte, hvis du har spørgsmål til behandlingen af dine oplysninger.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 11: Ændringer */}
              <div id="aendringer" className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm scroll-mt-24 transition-all hover:shadow-md">
                <div className="flex items-start gap-4 md:gap-6">
                  <div className="w-10 h-10 shrink-0 bg-[#123F13] text-white flex items-center justify-center rounded-xl">
                    <Edit size={20} />
                  </div>
                  <div className="flex-1 space-y-4">
                    <h3 className="font-serif text-lg md:text-xl font-bold text-[#123F13]">
                      Ændringer
                    </h3>
                    <div className="text-sm text-[#3B352F]/80 leading-relaxed space-y-3">
                      <p>
                        Jeg kan løbende opdatere denne privatlivspolitik, hvis der sker ændringer i mine systemer, min hjemmeside, mine ydelser eller gældende regler.
                      </p>
                      <p>
                        Den nyeste version vil altid være tilgængelig på hjemmesiden.
                      </p>
                      <p className="text-xs font-semibold text-[#8C2F12] pt-4">
                        Sidst opdateret: 1. juni 2026
                      </p>
                    </div>
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>
      </section>
    </>
  );
};

export default Privatlivspolitik;
